import {
  Grid,
  styled,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableSortLabel,
  Theme,
  useMediaQuery,
  useTheme,
  TableRow as MuiTableRow,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import React from 'react';
import { UnfoldMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import RotatingLoader from '../common/ui/RotatingLoader';
import { TableRow } from './TableRow';
import { CellProps, TableSortState } from './useTable';
import { getCellInlinePadding } from './cellInlinePadding';
import { mb } from '../common/theme/mbTokens';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
// it is done so we can omit price column for readonly users
export const TABLE_PRICE_ID = 'price';
export const TABLE_PHONE_ID = 'phone';

const StyledTable = styled(Table)(() => ({
  whiteSpace: 'pre',
  '& tbody': {
    '& tr': { '& td': { textTransform: 'capitalize' } },
  },
}));

type TableProps<T> = {
  isLoading: boolean;
  data?: T[];
  tableDetails: CellProps<T>[];

  hidePaging?: boolean;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  getRowSx?: (item: T) => SxProps<Theme> | undefined;
  splitColumnsOnWideScreen?: boolean;
  sort?: TableSortState;
  onToggleSort?: (sortBy: string) => void;
  /** Full-page tables: fill the parent's height (a flex column with minHeight 0) with the pager at the bottom. */
  fillHeight?: boolean;
  /** The table grows with its rows and the page around it scrolls: no vertical scroll of its own. */
  scrollWithPage?: boolean;
};

const getScrollSx = (fillHeight: boolean, scrollWithPage: boolean): SxProps<Theme> => {
  if (fillHeight) return { overflow: 'auto', flex: 1, minHeight: 0 };
  if (scrollWithPage) return { overflowX: 'auto' };
  return { overflow: 'auto', maxHeight: window.innerHeight < 674 ? '50dvh' : '70dvh' };
};

export function TableComponent<T>({
  isLoading,
  data,
  tableDetails,
  hidePaging,
  page,
  rowsPerPage,
  setRowsPerPage,
  setPage,
  getRowSx,
  splitColumnsOnWideScreen,
  sort,
  onToggleSort,
  fillHeight = false,
  scrollWithPage = false,
}: TableProps<T>) {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Two newspaper-style columns: read down the left half, then the right. The server already
  // returned the rows in order, so splitting keeps that sequence intact across both tables.
  const shouldSplit = !!splitColumnsOnWideScreen && isWideScreen && (data?.length ?? 0) > 3;
  const visibleTableDetails = tableDetails.filter((item) => !(item.id === TABLE_PRICE_ID && readOnly));
  const rowGroups = shouldSplit
    ? [data!.slice(0, Math.ceil(data!.length / 2)), data!.slice(Math.ceil(data!.length / 2))]
    : [data ?? []];

  const handleRowsPerPageChange = (event: any) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  return isLoading ? (
    <Grid
      container
      sx={{
        minHeight: '200px',
        ...(fillHeight && { flex: 1 }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <RotatingLoader />
    </Grid>
  ) : (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
      <Grid
        container
        sx={getScrollSx(fillHeight, scrollWithPage)}
        gap={shouldSplit ? 2 : 0}
        flexWrap="nowrap"
        alignItems="flex-start">
        {rowGroups.map((groupRows, groupIndex) => (
          <Grid item key={`group-${groupIndex}`} sx={{ flex: 1, minWidth: 0 }}>
            <StyledTable size="small" stickyHeader>
              <TableHead>
                <MuiTableRow>
                  {visibleTableDetails.map((item, cellIndex) => (
                    <TableCell
                      key={`head-${item.id}`}
                      align="center"
                      width={item.width ? `${item.width}px` : undefined}
                      sx={getCellInlinePadding(cellIndex, visibleTableDetails.length, item.noPadding)}>
                      <Grid container justifyContent="flex-start">
                        {item.sortKey && onToggleSort ? (
                          <TableSortLabel
                            active={sort?.sortBy === item.sortKey}
                            direction={sort?.sortBy === item.sortKey ? sort.sortDirection : 'asc'}
                            // A double arrow while unsorted, so the column reads as sortable
                            // before it is clicked -- MUI otherwise only reveals the arrow on
                            // hover, which is invisible on touch.
                            IconComponent={sort?.sortBy === item.sortKey ? undefined : UnfoldMore}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                opacity: sort?.sortBy === item.sortKey ? 1 : 0.5,
                              },
                            }}
                            onClick={() => onToggleSort(item.sortKey!)}>
                            {item.label}
                          </TableSortLabel>
                        ) : (
                          item.label
                        )}
                      </Grid>
                    </TableCell>
                  ))}
                </MuiTableRow>
              </TableHead>
              <TableBody>
                {groupRows.map((item: T, index: number) => (
                  <TableRow
                    key={`row-${groupIndex}-${index}`}
                    index={index}
                    tableDetails={tableDetails}
                    item={item}
                    rowSx={getRowSx?.(item)}
                  />
                ))}
              </TableBody>
            </StyledTable>
          </Grid>
        ))}
      </Grid>
      {!hidePaging && (
        // Sticky: the app shell is a fixed-height, non-scrolling column, so a pager left in normal
        // flow ends up below the fold on a phone -- unreachable, since a swipe over the table
        // scrolls the table's own container and never chains out to the page.
        <Grid
          container
          justifyContent="flex-end"
          sx={{
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
          <TablePagination
            sx={{ '& .MuiTablePagination-toolbar': { paddingInline: mb.layout.cardPadding } }}
            page={page}
            component="div"
            rowsPerPage={rowsPerPage}
            count={-1}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[10, 25, 50]}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage={isSmallScreen ? '' : t('orders.rows_per_page')}
            labelDisplayedRows={() => page}
            nextIconButtonProps={{ 'aria-label': 'Next Page', disabled: data && data.length < rowsPerPage }}
            backIconButtonProps={{ 'aria-label': 'Previous Page' }}
          />
        </Grid>
      )}
    </Grid>
  );
}
